import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { FormField, Select } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import {
  type Currency,
  type RateCardTemplate,
  buildRateCardOptions,
} from '../../utils/rateCardOptions.js';
import { HostsPicker } from './HostsPicker.js';

/**
 * 360 (#1147, contracts/supplier-change.md): move a car to another supplier. Pick the supplier, then one of ITS rate
 * cards (required: the car is never priced by another supplier's card) and, optionally, one of its hosts. The preview
 * says what moves before anything is written; Confirm sends one request, which the platform applies all at once.
 */
interface SupplierOption {
  _id: string;
  company_name: string;
}
interface HostOption {
  id: string;
  user: { id: string; name: string; email: string } | null;
}
interface Preview {
  from: { id: string; name: string };
  to: { id: string; name: string };
  moving: { rentals: number; legs: number; blocks: number };
  staying: { rentals: number };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function ChangeSupplierDialog({
  open,
  onClose,
  carId,
  currentSupplierId,
  onMoved,
}: {
  open: boolean;
  onClose: () => void;
  carId: string;
  currentSupplierId: string;
  onMoved: () => void;
}) {
  const [supplierId, setSupplierId] = useState('');
  const [rateCardId, setRateCardId] = useState('');
  // 377 (#1233): any number of the new supplier's hosts (design G).
  const [hostIds, setHostIds] = useState<string[]>([]);

  const suppliers = useQuery({
    queryKey: RESOURCE_KEYS.supplier.list,
    queryFn: () => api.get<{ items: SupplierOption[] }>('/suppliers'),
    enabled: open,
  });
  const preview = useQuery({
    queryKey: ['car-supplier-preview', carId, supplierId],
    queryFn: () =>
      api.get<Preview>(`/cars/${carId}/supplier-change/preview?supplier_id=${supplierId}`),
    enabled: open && Boolean(supplierId),
  });
  const rateCards = useQuery({
    queryKey: RESOURCE_KEYS.pricingTemplate.list,
    queryFn: () => api.get<{ items: RateCardTemplate[] }>('/pricing-templates'),
    enabled: open && Boolean(supplierId),
  });
  const currencies = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.get<Currency[]>('/currencies'),
    enabled: open && Boolean(supplierId),
  });
  const hosts = useQuery({
    queryKey: ['memberships', 'host-picker', supplierId],
    queryFn: () =>
      api.get<{ items: HostOption[] }>(
        `/memberships?supplier_id=${supplierId}&role=Host&employment_status=active`,
      ),
    enabled: open && Boolean(supplierId),
  });
  const cardOptions = supplierId
    ? buildRateCardOptions(rateCards.data?.items ?? [], currencies.data ?? [], supplierId)
    : [];

  const move = useMutation({
    mutationFn: () =>
      api.patch(`/cars/${carId}/supplier`, {
        supplier_id: supplierId,
        pricing_template_ids: [rateCardId],
        host_membership_ids: hostIds,
      }),
    onSuccess: () => {
      onMoved();
      close();
    },
  });

  function close() {
    setSupplierId('');
    setRateCardId('');
    setHostIds([]);
    move.reset();
    onClose();
  }

  const p = preview.data;
  return (
    <Modal
      open={open}
      onClose={close}
      title="Change supplier"
      footer={
        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            loading={move.isPending}
            disabled={!supplierId || !rateCardId || !p}
            onClick={() => move.mutate()}
          >
            Move car
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <FormField label="New supplier" name="new_supplier_id">
          <Select
            id="new_supplier_id"
            value={supplierId}
            onChange={(e) => {
              setSupplierId((e.target as HTMLSelectElement).value);
              setRateCardId('');
              setHostIds([]);
              move.reset();
            }}
          >
            <option value="">Select a supplier…</option>
            {suppliers.data?.items
              ?.filter((s) => s._id !== currentSupplierId)
              .map((s) => (
                <option key={s._id} value={s._id}>
                  {s.company_name}
                </option>
              ))}
          </Select>
        </FormField>
        {supplierId && (
          <>
            <FormField label="Rate card" name="new_rate_card_id" required>
              <Select
                id="new_rate_card_id"
                value={rateCardId}
                onChange={(e) => setRateCardId((e.target as HTMLSelectElement).value)}
              >
                <option value="">Select a rate card…</option>
                {cardOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FormField>
            {rateCards.data && cardOptions.length === 0 && (
              <p data-testid="no-rate-card" className="text-sm text-red-600">
                This supplier has no rate card yet. Create one for them first.
              </p>
            )}
            <HostsPicker
              label="Hosts at the new supplier (optional)"
              hint="The car's current hosts belong to the old supplier, so they are removed; pick new ones here or later on the car."
              emptyText="No host"
              value={hostIds}
              onChange={setHostIds}
              options={(hosts.data?.items ?? []).map((m) => ({
                id: m.id,
                name: m.user?.name || m.user?.email || 'Unnamed',
              }))}
            />
          </>
        )}
        {p && (
          <div data-testid="supplier-change-preview" className="rounded-lg bg-surface p-3 text-sm">
            <p>
              {plural(p.moving.rentals, 'upcoming rental', 'upcoming rentals')} and{' '}
              {plural(p.moving.blocks, 'block', 'blocks')} on the calendar move to {p.to.name}.
            </p>
            <p className="mt-1 text-steel">
              {plural(
                p.staying.rentals,
                'started or past rental stays',
                'started or past rentals stay',
              )}{' '}
              with {p.from.name}, together with the money they earned. The car's current host is
              removed unless you choose one of {p.to.name}'s hosts.
            </p>
          </div>
        )}
        {preview.isError && (
          <p className="text-sm text-red-600">{(preview.error as Error).message}</p>
        )}
        {move.isError && (
          <p role="alert" className="text-sm text-red-600">
            {(move.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  );
}
